import React from 'react';

interface Step3AdvancedSettingsProps {
  formData: {
    temperature: number;
    maxTokens: number;
    model: string;
  };
  updateFormData: (key: string, value: any) => void;
  models: any[];
}

const Step3AdvancedSettings: React.FC<Step3AdvancedSettingsProps> = ({ formData, updateFormData, models }) => {
  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl p-4 sm:p-6 space-y-6">
        <h3 className="text-lg font-medium text-gray-900">Model Parameters</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
            <span>Temperature: {formData.temperature}</span>
            <div className="relative group">
              <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center cursor-help">
                <span className="text-xs text-gray-500">?</span>
              </div>
              <div className="absolute left-6 top-0 w-64 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Controls randomness in responses. Lower values (0.1) make output more focused and deterministic. Higher values (0.9) make output more creative and varied.
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={formData.temperature}
            onChange={(e) => updateFormData('temperature', parseFloat(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Focused</span>
            <span>Creative</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
            <span>Max Output Length</span>
            <div className="relative group">
              <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center cursor-help">
                <span className="text-xs text-gray-500">?</span>
              </div>
              <div className="absolute left-6 top-0 w-64 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Maximum length of the AI's response. Longer responses allow for more detailed answers but use more tokens. 1 token ≈ 0.75 words.
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          </label>
          <select
            value={formData.maxTokens}
            onChange={(e) => updateFormData('maxTokens', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal"
          >
            <option value={1000}>Short responses (~2 pages)</option>
            <option value={2000}>Medium responses (~4 pages)</option>
            <option value={4000}>Long responses (~8 pages)</option>
            <option value={8000}>Very long responses (~16 pages)</option>
            <option value={16000}>Extended responses (~32 pages)</option>
            <option value={32000}>Comprehensive responses (~65 pages)</option>
            {(models.find(m => m.id === formData.model)?.maxTokens ?? 0) >= 65536 && (
              <option value={65000}>Maximum length (~130 pages)</option>
            )}
          </select>
        </div>
      </div>
    </div>
  );
};

export default Step3AdvancedSettings;
